import { useEffect, useState } from "react";
import { useTranslation } from "../hooks/useTranslation";
import { useAppStore } from "../store/appStore";
import appStyles from "../styles/styles";

interface RecorderControlsProps {
	onToggleRecording: () => void;
	/** 表示するマイク名（表示しない場合は null） */
	micLabel: string | null;
	/** 表示する認識言語名（表示しない場合は null） */
	languageLabel: string | null;
}

const formatTime = (seconds: number): string => {
	const mins = Math.floor(seconds / 60)
		.toString()
		.padStart(2, "0");
	const secs = (seconds % 60).toString().padStart(2, "0");
	return `${mins}:${secs}`;
};

/** 録音ボタンと録音状態の表示 */
export const RecorderControls = ({ onToggleRecording, micLabel, languageLabel }: RecorderControlsProps) => {
	const isRecording = useAppStore((state) => state.isRecording);
	const t = useTranslation();
	const [recordingTime, setRecordingTime] = useState<number>(0);

	// 録音中は1秒ごとに経過時間を更新
	useEffect(() => {
		let interval: number | null = null;
		if (isRecording) {
			setRecordingTime(0);
			interval = window.setInterval(() => {
				setRecordingTime((prev) => prev + 1);
			}, 1000);
		} else {
			setRecordingTime(0);
		}
		return () => {
			if (interval) clearInterval(interval);
		};
	}, [isRecording]);

	return (
		<section className={appStyles.recordingSection}>
			<button
				type="button"
				className={appStyles.recordButton(isRecording)}
				onClick={onToggleRecording}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						onToggleRecording();
					}
				}}
				aria-label={isRecording ? t.stopRecordingAriaLabel : t.startRecordingAriaLabel}
			>
				<span className={appStyles.recordIcon}>{isRecording ? "■" : "●"}</span>
			</button>
			<div className="text-base text-blue-300 font-semibold mt-2">
				{isRecording ? (
					<>
						{t.recordingInProgress}
						<span className="font-mono">（{formatTime(recordingTime)}）</span>
					</>
				) : (
					t.recordingReady
				)}
			</div>
			{/* マイク情報と言語情報を横に並べる */}
			<div className="flex flex-wrap items-center justify-center gap-4 mt-1">
				{micLabel !== null && (
					<div className="text-xs text-gray-400">
						{t.micPrefix}{micLabel}
					</div>
				)}
				{languageLabel !== null && (
					<div className="text-xs text-gray-400">
						{t.recognitionLanguagePrefix}{languageLabel}
					</div>
				)}
			</div>
		</section>
	);
};

export default RecorderControls;
